// Транспорт вкладки «Поля карточек» (/api/admin/card-fields*): владелец сам добавляет,
// правит и архивирует поля карточек сделок/компаний/контактов/судов — без программиста.
// Дверь та же, что у учёток и наборов, — поимённая учётка владельца; 403 здесь означает
// «не владелец», и это не поломка.
//
// ═══ ЗАГЛУШКИ-КОНТРАКТЫ. Ручки ядра строит ПАРАЛЛЕЛЬНЫЙ агент — поэтому все вызовы
// собраны В ОДНОМ ЭТОМ ФАЙЛЕ, и у каждого записана TODO-форма запроса и ответа.
// Если ядро отдаст другую форму — менять здесь и в web/bff/admin_users.py, больше нигде.
//
// ПРО ДАННЫЕ, одним абзацем: поле появляется на карточках сразу после сохранения;
// значения хранятся в CRM (своя схема ядра) и в Bitrix НЕ ПИШУТСЯ. Удаления полей нет
// вовсе — только архив: поле исчезает с карточек, а накопленные значения не стираются.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Unauthorized } from './client'

const БАЗА = '/api/admin/card-fields'

/** Карточки, на которых владелец заводит поля. Коды — ключи спеки карточек ядра
 *  (cards_spec: 'сделка', 'компания', 'контакт', 'судно'), не выдуманные. */
export type Сущность = 'сделка' | 'компания' | 'контакт' | 'судно'

export const СУЩНОСТИ: { код: Сущность; имя: string }[] = [
  { код: 'сделка', имя: 'Сделки' },
  { код: 'компания', имя: 'Компании' },
  { код: 'контакт', имя: 'Контакты' },
  { код: 'судно', имя: 'Суда' },
]

/** Типы полей, которые владелец может завести. Закрытый список — он же в BFF. */
export type ТипПоля = 'text' | 'number' | 'date' | 'list' | 'checkbox' | 'url'

export const ТИПЫ: { код: ТипПоля; имя: string }[] = [
  { код: 'text', имя: 'текст' },
  { код: 'number', имя: 'число' },
  { код: 'date', имя: 'дата' },
  { код: 'list', имя: 'список' },
  { код: 'checkbox', имя: 'галочка' },
  { код: 'url', имя: 'ссылка' },
]

/** Одно поле карточки — системное (из спеки, builtin: true) или владельца. */
export interface ПолеКарточки {
  entity: Сущность
  /** Слово латиницей: ^[a-z][a-z0-9_]*$. У существующего поля код не меняется —
   *  к нему привязаны накопленные значения. */
  code: string
  title: string
  type: ТипПоля
  /** Варианты — только у type 'list'. */
  options?: string[]
  /** Код группы-секции на карточке (куда поле встаёт). */
  group: string
  required: boolean
  order: number
  /** Архивное поле с карточек убрано, данные не стёрты. */
  archived: boolean
  /** Системное поле из спеки карточки: показывается серым, не правится. */
  builtin: boolean
}

export interface ОбзорПолей {
  fields: ПолеКарточки[]
  /** Группы-секции карточки (из спеки) — для выбора «куда встаёт поле». */
  groups?: { code: string; title: string }[]
}

/** Что уходит при сохранении (добавление и правка — одна форма). Без archived и
 *  builtin: архив — отдельная ручка, builtin ставит только ядро. */
export interface ПравкаПоля {
  entity: Сущность
  code: string
  title: string
  type: ТипПоля
  options?: string[]
  group: string
  required: boolean
  order: number
}

/** Правило кода поля — то же, что в BFF (admin_users._КОД_ПОЛЯ): слово латиницей. */
export const КОД_ПОЛЯ = /^[a-z][a-z0-9_]{0,63}$/

// Транслитерация названий в коды — та же манера, что у кодов Bitrix в зеркале
// ('kontraktnoe_proizvodstvo', 'buhgalteriya', 'koordinaciya' — см. cards_spec).
const ТРАНСЛИТ: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i',
  й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't',
  у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '',
  э: 'e', ю: 'yu', я: 'ya',
}

/** «Порт приписки» → 'port_pripiski': заготовка кода из названия. Человек может
 *  поправить руками — это только подстановка, а не правило. */
export function кодИзНазвания(название: string): string {
  let s = ''
  for (const ч of название.toLowerCase()) {
    if (ч in ТРАНСЛИТ) s += ТРАНСЛИТ[ч]
    else if (/[a-z0-9]/.test(ч)) s += ч
    else s += '_'
  }
  return s
    .replace(/_+/g, '_')       // подряд идущие разделители — в один
    .replace(/^[^a-z]+/, '')   // код начинается с буквы
    .replace(/_+$/, '')
    .slice(0, 64)
}

async function запрос<T>(путь: string, init?: RequestInit): Promise<T> {
  const res = await fetch(БАЗА + путь, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Учётки и доступ» выдаётся не по роли, а по учётке '
        + 'владельца — как касса. Вам он не открыт.')
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

const КЛЮЧ = 'admin-card-fields'

/**
 * Список полей сущности: системные (builtin, из спеки карточки) + поля владельца,
 * включая архивные.
 *
 * TODO(ядро): GET /api/admin/card-fields?entity=сделка
 *   ответ 200: { fields: ПолеКарточки[], groups?: [{code, title}] }
 *   (поля уже отсортированы: группы в порядке спеки, внутри группы по order).
 */
export function useПоляКарточек(entity: Сущность) {
  return useQuery<ОбзорПолей>({
    queryKey: [КЛЮЧ, entity],
    queryFn: () => запрос<ОбзорПолей>(`?entity=${encodeURIComponent(entity)}`),
    staleTime: 10_000,
    retry: 1,
  })
}

/** После успеха перечитать поля всех сущностей — дешевле, чем угадывать, чью правку задели. */
function useДействие<Т, Р>(ф: (арг: Т) => Promise<Р>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ф,
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [КЛЮЧ] }) },
  })
}

/**
 * Сохранить поле: добавление и правка — одна форма, ядро различает их по code.
 *
 * TODO(ядро): POST /api/admin/card-fields/save
 *   тело: ПравкаПоля (options — только при type 'list')
 *   ответ 200: { ok: true, field: ПолеКарточки }
 *   отказы: 400 (код не слово / совпал с системным / нет вариантов у списка),
 *           403 (не владелец) — текст в {error}.
 */
export function useСохранениеПоля() {
  return useДействие((арг: ПравкаПоля) =>
    запрос<{ ok: boolean; field: ПолеКарточки }>('/save', {
      method: 'POST',
      body: JSON.stringify(арг),
    }))
}

/**
 * Архив вместо удаления: archived: true убирает поле с карточек, false — возвращает.
 * Значения не стираются ни в какую сторону.
 *
 * TODO(ядро): POST /api/admin/card-fields/archive
 *   тело: { entity, code, archived: boolean }
 *   ответ 200: { ok: true, field: ПолеКарточки }
 *   отказы: 400 (системное поле не архивируется), 404 (поля нет), 403 (не владелец).
 */
export function useАрхивПоля() {
  return useДействие((арг: { entity: Сущность; code: string; archived: boolean }) =>
    запрос<{ ok: boolean; field: ПолеКарточки }>('/archive', {
      method: 'POST',
      body: JSON.stringify(арг),
    }))
}
